import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  Award,
  Clock,
  Pencil,
  Tractor,
  UserX,
} from "lucide-react";
import type { WorkerDetail } from "@crewledger/shared/schemas";
import {
  CommandCenterCard,
  ConfirmationModal,
  EmptyState,
  PageHeader,
  StatCard,
  StatusBadge,
  useToast,
} from "@/components/design";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { deactivateWorker, fetchWorker } from "./api";
import { PrivacyHelper, WorkerStatusBadge } from "./components/worker-status-badge";
import { WorkerCertificationsSection } from "./components/worker-certifications-section";
import { WorkerDetailTabs } from "./components/worker-detail-tabs";
import { useWorkerPermissions } from "./use-worker-permissions";

function mapEntryStatus(status: string): "Draft" | "Submitted" | "Approved" | "Rejected" | "Locked" {
  switch (status) {
    case "SUBMITTED":
      return "Submitted";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    case "LOCKED":
      return "Locked";
    default:
      return "Draft";
  }
}

export function WorkerDetailPage() {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canViewPayroll } = useWorkerPermissions();
  const [worker, setWorker] = useState<WorkerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const load = () => {
    if (!workerId) return;
    setLoading(true);
    fetchWorker(workerId)
      .then((res) => { setWorker(res.data); })
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : "Failed to load worker"); })
      .finally(() => { setLoading(false); });
  };

  useEffect(load, [workerId]);

  const handleDeactivate = async () => {
    if (!workerId) return;
    setDeactivating(true);
    try {
      await deactivateWorker(workerId);
      toast({ variant: "success", title: "Worker deactivated" });
      setDeactivateOpen(false);
      load();
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to deactivate",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setDeactivating(false);
    }
  };

  if (error && !worker) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Worker not found"
        description={error}
        actionLabel="Back to workers"
        onAction={() => navigate("/workers")}
      />
    );
  }

  if (loading || !worker) {
    return <div className="h-96 animate-pulse rounded-xl border bg-card-white" />;
  }

  const { payPeriodSummary: pp } = worker;

  return (
    <div className="space-y-6 lg:space-y-8">
      <PageHeader
        title={worker.displayName}
        description={`${worker.employeeId}${worker.trade ? ` · ${worker.trade}` : ""}${worker.crew ? ` · ${worker.crew}` : ""}`}
        badge={<WorkerStatusBadge status={worker.status} />}
        actions={
          worker.canEdit && worker.status === "ACTIVE" ? (
            <div className="flex gap-2">
              <Link to={`/workers/${worker.id}/edit`}>
                <Button variant="outline" size="sm">
                  <Pencil className="mr-1.5 h-4 w-4" />
                  Edit
                </Button>
              </Link>
              <Button variant="destructive" size="sm" onClick={() => { setDeactivateOpen(true); }}>
                <UserX className="mr-1.5 h-4 w-4" />
                Deactivate
              </Button>
            </div>
          ) : undefined
        }
      />

      <WorkerDetailTabs />

      <PrivacyHelper />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Period hours"
          value={formatHours(pp.hours.total)}
          subtext={pp.payPeriod?.label}
          icon={Clock}
          accent="navy"
        />
        <StatCard label="Regular" value={formatHours(pp.hours.regular)} icon={Clock} accent="navy" />
        <StatCard label="Overtime" value={formatHours(pp.hours.overtime)} icon={Clock} accent="amber" />
        <StatCard label="Double time" value={formatHours(pp.hours.doubleTime)} icon={Clock} accent="amber" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <CommandCenterCard title="Profile" icon={Award} className="lg:col-span-1">
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Employee ID</dt>
              <dd className="font-mono font-semibold text-navy-deep">{worker.employeeId}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Internal name</dt>
              <dd className="font-medium text-navy-deep">{worker.internalName}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Site</dt>
              <dd>{worker.siteName ? `${worker.siteCode} — ${worker.siteName}` : "Unassigned"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Pay rule profile</dt>
              <dd>{worker.ruleProfileName ?? "Default"}</dd>
            </div>
            {canViewPayroll && worker.baseHourlyRate != null ? (
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Base rate</dt>
                <dd className="font-semibold text-orange-construction">
                  {formatCurrency(worker.baseHourlyRate)}/hr
                </dd>
              </div>
            ) : null}
            {pp.grossPay != null && canViewPayroll ? (
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Period gross</dt>
                <dd className="font-semibold">{formatCurrency(pp.grossPay)}</dd>
              </div>
            ) : null}
            {worker.notes ? (
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Notes</dt>
                <dd className="whitespace-pre-wrap">{worker.notes}</dd>
              </div>
            ) : null}
          </dl>
        </CommandCenterCard>

        <div className="space-y-6 lg:col-span-2">
          <CommandCenterCard title="Time entries" description="Recent labor log" icon={Clock}>
            {worker.timeEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No time entries yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="pb-2">Date</th>
                      <th className="pb-2">Site</th>
                      <th className="pb-2">Hours</th>
                      <th className="pb-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {worker.timeEntries.map((e) => (
                      <tr key={e.id} className="border-b border-border/60 last:border-0">
                        <td className="py-2">{e.date}</td>
                        <td className="py-2">{e.siteName ?? "—"}</td>
                        <td className="py-2 tabular-nums">{formatHours(e.hours)}</td>
                        <td className="py-2">
                          <StatusBadge status={mapEntryStatus(e.status)} showDot={false} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CommandCenterCard>

          <CommandCenterCard title="Equipment used" icon={Tractor}>
            {worker.equipmentUsage.length === 0 ? (
              <p className="text-sm text-muted-foreground">No equipment usage logged.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {worker.equipmentUsage.map((u) => (
                  <li key={u.id} className="flex justify-between rounded-md bg-bg-light px-3 py-2">
                    <span className="font-medium text-navy-deep">{u.equipmentName}</span>
                    <span className="text-muted-foreground">
                      {u.date} · {formatHours(u.usageHours)}h
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CommandCenterCard>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <WorkerCertificationsSection
          workerId={worker.id}
          canEdit={worker.canEdit}
          certifications={worker.certifications}
          onUpdated={load}
        />

        <CommandCenterCard title="Audit history">
          {worker.auditActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground">No audit events yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {worker.auditActivity.map((a) => (
                <li key={a.id} className="border-b border-border/60 pb-2 last:border-0">
                  <p className="font-medium text-navy-deep">{a.action.replaceAll(".", " · ")}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.actorName ?? "System"} · {new Date(a.createdAt).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </CommandCenterCard>
      </div>

      <ConfirmationModal
        open={deactivateOpen}
        onClose={() => { setDeactivateOpen(false); }}
        onConfirm={() => void handleDeactivate()}
        title="Deactivate worker?"
        description="Inactive workers won't appear in daily entry pickers. Historical time and pay data is preserved."
        confirmLabel="Deactivate"
        variant="destructive"
        loading={deactivating}
      />
    </div>
  );
}
